import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FaIcons from 'react-icons/fa';

const DonationTier = ({ amount, desc, selected, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-6 rounded-2xl border-2 text-left transition-all duration-300 relative overflow-hidden ${
      selected
        ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20'
        : 'border-gray-100 bg-white hover:border-primary/50'
    }`}
  >
    {selected && (
      <div className="absolute top-0 right-0 p-2 text-primary">
        <SafeIcon icon={FaIcons.FaCheckCircle} />
      </div>
    )}
    <div className="text-3xl font-heading font-bold text-primary-dark mb-2">${amount}</div>
    <p className="text-gray-600 text-sm font-medium">{desc}</p>
  </motion.button>
);

const GetInvolved = () => {
  const [amount, setAmount] = useState(50);
  const [frequency, setFrequency] = useState('monthly');
  const paypalEmail = "Agir.usa.2025@gmail.com";

  const handleDonate = () => {
    const baseUrl = "https://www.paypal.com/cgi-bin/webscr";
    const returnUrl = window.location.origin + "/thank-you";
    const cancelUrl = window.location.href;

    const commonParams = {
      business: paypalEmail,
      currency_code: "USD",
      no_shipping: "1",
      return: returnUrl,
      cancel_return: cancelUrl,
      lc: "US", // Locale
      charset: "UTF-8"
    };

    let params;

    if (frequency === 'monthly') {
      // Recurring Subscription Configuration
      params = new URLSearchParams({
        ...commonParams,
        cmd: "_xclick-subscriptions",
        item_name: `Monthly Support for AGIR USA ($${amount}/mo)`,
        a3: amount.toString(), // Subscription Price
        p3: "1", // Subscription Duration
        t3: "M", // Subscription Units (M=Month)
        src: "1", // Recurring payments (1=Yes)
        sra: "1", // Reattempt on failure (1=Yes)
        no_note: "1" // Hide note field
      });
    } else {
      // One-Time Donation Configuration
      params = new URLSearchParams({
        ...commonParams,
        cmd: "_donations",
        item_name: "Donation to AGIR USA",
        amount: amount.toString(),
        tax: "0"
      });
    }

    window.open(`${baseUrl}?${params.toString()}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Donate & Get Involved | AGIR USA</title>
        <meta name="description" content="Support AGIR USA's mission in Guinea-Bissau. Your donations provide life-changing meals and education." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative py-32 md:py-40 flex items-center justify-center overflow-hidden min-h-[50vh]">
        <div className="absolute inset-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/c4fccb3d7d3b5939606711b6ca44aa9e.jpeg" 
            alt="Sharing love" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-primary-dark/85 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold mb-6">Invest in a Future</h1>
            <p className="text-xl max-w-2xl mx-auto mb-8 font-light text-gray-200">
              Your generosity provides meals, education, and hope to children in Guinea-Bissau who need it most.
            </p>
          </motion.div>
        </div>
      </div>

      <Section className="-mt-20 relative z-20 pt-0">
        <div className="grid lg:grid-cols-2 gap-16">
          
          {/* Donation Form - Elevated Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-gray-100 relative overflow-hidden">
              {/* Decorative background blur */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/5 rounded-full blur-3xl -z-10"></div>
              
              <h2 className="font-heading text-3xl font-bold mb-8 text-center text-primary-dark">Make a Difference Today</h2>
              
              <div className="flex bg-gray-100 p-1.5 rounded-full mb-8">
                <button 
                  className={`flex-1 py-3 rounded-full font-bold text-sm transition-all duration-300 ${frequency === 'monthly' ? 'bg-white shadow-md text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setFrequency('monthly')}
                >
                  Monthly Gift
                </button>
                <button 
                  className={`flex-1 py-3 rounded-full font-bold text-sm transition-all duration-300 ${frequency === 'once' ? 'bg-white shadow-md text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setFrequency('once')}
                >
                  One-Time Gift
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { val: 30, text: "Feeds a child for a month" },
                  { val: 50, text: "Supplies + Daily Meals" },
                  { val: 100, text: "Supports 3 children" },
                  { val: 250, text: "Community project" }
                ].map((tier) => (
                  <DonationTier 
                    key={tier.val} 
                    amount={tier.val} 
                    desc={tier.text} 
                    selected={amount === tier.val}
                    onClick={() => setAmount(tier.val)}
                  />
                ))}
              </div>

              <div className="mb-8">
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest">Custom Amount (USD)</label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-xl">$</span>
                  <input 
                    type="number" 
                    className="w-full pl-10 pr-4 py-4 border-2 border-gray-100 rounded-xl focus:border-primary outline-none transition-colors text-xl font-bold text-primary-dark group-hover:border-gray-200"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                  />
                </div>
              </div>

              <Button 
                onClick={handleDonate}
                className="w-full bg-secondary hover:bg-secondary-dark text-white font-bold py-5 rounded-xl shadow-lg hover:shadow-glow transition-all flex items-center justify-center gap-3 text-lg"
              >
                <SafeIcon icon={FaIcons.FaPaypal} />
                {frequency === 'monthly' ? `Donate $${amount} Monthly` : `Donate $${amount} Now`}
              </Button>

              <div className="mt-6 flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-400 font-bold tracking-wider">
                  <SafeIcon icon={FaIcons.FaLock} /> SECURE ENCRYPTED TRANSACTION
                </div>
                <div className="flex gap-2 opacity-50">
                  <SafeIcon icon={FaIcons.FaCcVisa} className="text-xl" />
                  <SafeIcon icon={FaIcons.FaCcMastercard} className="text-xl" />
                  <SafeIcon icon={FaIcons.FaCcAmex} className="text-xl" />
                  <SafeIcon icon={FaIcons.FaPaypal} className="text-xl" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Impact Copy & Additional Options - Staggered Reveal */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-12 py-10"
          >
            <div>
              <div className="inline-block p-3 bg-secondary/10 rounded-xl mb-6">
                <SafeIcon icon={FaIcons.FaHandHoldingHeart} className="text-secondary text-3xl" />
              </div>
              <h3 className="font-heading text-4xl font-bold text-primary-dark mb-6">Where Your Money Goes</h3>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                92% of every dollar donated goes directly to our field programs in Guinea-Bissau. Your gift is transformed into warm meals, school uniforms, teacher salaries, and clean facilities.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <motion.div whileHover={{ y: -5 }} className="bg-white p-6 rounded-2xl shadow-soft border-l-4 border-primary">
                  <div className="font-heading font-bold text-3xl text-primary-dark mb-1">$1.00</div>
                  <div className="text-sm text-gray-500 font-medium">Provides one full nutritious meal</div>
                </motion.div>
                <motion.div whileHover={{ y: -5 }} className="bg-white p-6 rounded-2xl shadow-soft border-l-4 border-secondary">
                  <div className="font-heading font-bold text-3xl text-primary-dark mb-1">$15.00</div>
                  <div className="text-sm text-gray-500 font-medium">Provides a complete school uniform</div>
                </motion.div>
              </div>
            </div>

            {/* Zelle Donation Card */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#6d1ed4]/5 rounded-bl-full pointer-events-none"></div>
              
              <div className="flex flex-col sm:flex-row gap-8 items-center">
                <div className="flex-1 text-center sm:text-left">
                   <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#6d1ed4]/10 text-[#6d1ed4] font-bold text-xs tracking-widest uppercase rounded-full mb-4">
                    <SafeIcon icon={FaIcons.FaMobileAlt} /> Direct Transfer
                   </div>
                   <h3 className="font-heading text-2xl font-bold text-primary-dark mb-3">Give via Zelle®</h3>
                   <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                     Prefer to give through Zelle? You can scan the QR code to support our mission directly with 0% processing fees.
                   </p>
                   <div className="inline-block bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                     <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Recipient</p>
                     <p className="text-primary-dark font-bold text-sm select-all">Agir.usa.2025@gmail.com</p>
                   </div>
                </div>
                
                <div className="shrink-0 relative">
                  <div className="w-40 h-40 bg-white p-2 rounded-xl shadow-md border border-gray-100 flex items-center justify-center">
                    <img 
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1771870384632-image.png" 
                      alt="Zelle QR Code" 
                      className="w-full h-full object-contain mix-blend-multiply" 
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-secondary text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">
                    SCAN ME
                  </div>
                </div>
              </div>
            </div>

            {/* Sponsorship Card */}
            <div className="bg-primary-dark text-white p-10 rounded-3xl relative overflow-hidden shadow-2xl">
              <div className="relative z-10">
                <h3 className="font-heading text-2xl font-bold mb-4 text-secondary">Sponsor a Child</h3>
                <p className="text-gray-300 mb-6 italic text-lg">
                  "Giving isn't just about making a donation. It's about making a difference."
                </p>
                <p className="text-gray-300 mb-8">
                  $30 a month covers all basic needs for a child in our program. You'll receive updates on their progress.
                </p>
                <Button 
                  variant="secondary"
                  onClick={() => {
                    setFrequency('monthly');
                    setAmount(30);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Start Monthly Sponsorship
                </Button>
              </div>
              <div className="absolute -bottom-10 -right-10 opacity-5 rotate-12">
                <SafeIcon icon={FaIcons.FaGlobeAfrica} className="text-[200px]" />
              </div>
            </div>

          </motion.div>
        </div>
      </Section>
    </>
  );
};

export default GetInvolved;